import { useMemo } from 'react';
import { useDebounce } from 'react-use';
import { useDispatch } from '../store';
import { useFedopsLogger } from '../components/Provider/Fedops/FedopsProvider';
import { loadPaymentMethods } from '../actions/loadPaymentMethods';
import { getPageFingerprint } from '../utils/sentry/getPageFingerprint';
import { logGeneralError } from '../utils/sentry';
import { getAppIdentity } from '../utils/preloadData';
import { InteractionName } from '../types/InteractionName';
import { type WidgetProps } from '../types';

export const useFetchPaymentMethods = (props: WidgetProps) => {
  const { paymentAmount: amount, currency, meta, revalidateAppInstance } = props;
  const dispatch = useDispatch();
  const fedopsLogger = useFedopsLogger();
  const appIdentity = useMemo(() => getAppIdentity(props), [meta, revalidateAppInstance]);
  /*
   * Debounce is needed on the cart page and the product page,
   * since there is an option to change the currency and the quantity of the product (which affects the amount)
   */
  const DEBOUNCE_TIMOUT_IN_MS = getPageFingerprint() === 'checkout-page' ? 0 : 700;

  useDebounce(
    () => {
      fedopsLogger.interactionStarted(InteractionName.LoadPaymentMethods);

      dispatch(loadPaymentMethods({ appIdentity, currency, amount }))
        .unwrap()
        .then(() => {
          fedopsLogger.interactionEnded(InteractionName.LoadPaymentMethods);
        })
        .catch((error) => {
          logGeneralError({
            message: 'Failed to load payment methods',
            payload: error,
          });
        });
    },
    DEBOUNCE_TIMOUT_IN_MS,
    [currency, amount],
  );
};
